import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { 
  TrendingUp, 
  TrendingDown, 
  RefreshCw,
  Sparkles,
  Briefcase,
  DollarSign,
  PieChart,
  Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PortfolioSummary } from "@/components/PortfolioSummary";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import CrashAlertBanner from "@/components/CrashAlertBanner";
import StarredPlaysStrip from "@/components/StarredPlaysStrip";
import NewsStrip from "@/components/NewsStrip";
import EmergentPromiseStrip from "@/components/EmergentPromiseStrip";

export default function Dashboard() {
  const [portfolio, setPortfolio] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const portfolioRes = await api.get("/portfolio/summary");
      setPortfolio(portfolioRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch portfolio data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInsights = useCallback(async () => {
    try {
      const response = await api.get("/insights");
      setInsights(response.data);
    } catch (error) {
      console.error("Error fetching insights:", error);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    await fetchInsights();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  useEffect(() => {
    fetchData();
    fetchInsights();
    
    // Refresh portfolio every 2 minutes
    const portfolioInterval = setInterval(fetchData, 120000);
    // Refresh insights every 5 minutes
    const insightInterval = setInterval(fetchInsights, 300000);
    
    return () => {
      clearInterval(portfolioInterval);
      clearInterval(insightInterval);
    };
  }, [fetchData, fetchInsights]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 skeleton rounded-sm" />
          ))}
        </div>
        <div className="h-64 skeleton rounded-sm" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      {/* 🚨 CRASH ALERT BANNER - Always on top */}
      <CrashAlertBanner />

      {/* ⭐ Starred plays strip (deep-links into Sohel Pick) */}
      <StarredPlaysStrip />

      {/* 💎 Emergent Promise strip (high-conviction picks) */}
      <EmergentPromiseStrip />

      {/* 📰 News strip (deep-links into full /news page) */}
      <NewsStrip />

      {/* Header - Mobile Optimized */}
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="font-bold text-xl sm:text-2xl lg:text-3xl text-white">
            SP WEALTH TRACKER
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            Your portfolio overview and AI insights
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2 w-full sm:w-auto border-gray-700 text-gray-300"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Portfolio Summary */}
      <PortfolioSummary data={portfolio} />

      {/* AI Market Insights */}
      {insights && (
        <Card className="card-dark border-ai/30" data-testid="ai-insights-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading flex items-center gap-2 text-ai">
              <Sparkles className="w-5 h-5" />
              AI MARKET INSIGHTS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Market Sentiment */}
              <div className="p-4 bg-black/30 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-ai" />
                  <span className="text-sm font-bold">Market Sentiment</span>
                </div>
                <Badge className={
                  insights.sentiment === 'bullish' ? 'bg-profit text-black' :
                  insights.sentiment === 'bearish' ? 'bg-loss' : 'bg-warning text-black'
                }>
                  {insights.sentiment?.toUpperCase() || 'NEUTRAL'}
                </Badge>
                {insights.sentiment_score && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Confidence: {insights.sentiment_score}%
                  </p>
                )}
              </div>

              {/* Key Insight */}
              <div className="p-4 bg-black/30 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-ai" />
                  <span className="text-sm font-bold">Key Insight</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {insights.insights || insights.summary || "Analyzing market conditions..."}
                </p>
              </div>
            </div>

            {/* Portfolio Recommendations */}
            {insights.recommendations && (
              <div className="mt-4 p-4 bg-ai/10 rounded border border-ai/30">
                <p className="text-sm font-bold text-ai mb-2">Portfolio Action</p>
                <p className="text-sm text-muted-foreground">{insights.recommendations}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-dark" data-testid="portfolio-value-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-ai/20 rounded">
                <DollarSign className="w-6 h-6 text-ai" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Portfolio Value</p>
                <p className="font-data text-2xl font-bold">
                  ${portfolio?.total_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark" data-testid="total-invested-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-profit/20 rounded">
                <Briefcase className="w-6 h-6 text-profit" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Invested</p>
                <p className="font-data text-2xl font-bold">
                  ${portfolio?.total_invested?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark" data-testid="pnl-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded ${portfolio?.total_pnl >= 0 ? 'bg-profit/20' : 'bg-loss/20'}`}>
                {portfolio?.total_pnl >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-profit" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-loss" />
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total P&L</p>
                <p className={`font-data text-2xl font-bold ${portfolio?.total_pnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                  {portfolio?.total_pnl >= 0 ? '+' : ''}${portfolio?.total_pnl?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                </p>
                <p className={`text-xs ${portfolio?.total_pnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                  ({portfolio?.total_pnl_pct >= 0 ? '+' : ''}{portfolio?.total_pnl_pct?.toFixed(2) || '0.00'}%)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Asset Count */}
      <Card className="card-dark">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-ai/20 rounded">
                <PieChart className="w-6 h-6 text-ai" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Assets Tracked</p>
                <p className="font-data text-2xl font-bold">{portfolio?.asset_count || 0}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/portfolio'}>
              View Portfolio
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
